/* Insmia cart-drawer recommendations (native Section Rendering cards).
   The product template's section id contains a Shopify-generated template
   prefix, so the id is discovered from a cart item's product page and cached
   only after discovery succeeds.
 */
(function () {
  var SID_KEY = 'ins-recs-sid';
  var timer = null;
  var wanted = false;
  var armed = false;

  function getSid() {
    try {
      return localStorage.getItem(SID_KEY) || null;
    } catch (e) {
      return null;
    }
  }

  function setSid(value) {
    try {
      localStorage.setItem(SID_KEY, value);
    } catch (e) {
      /* Storage may be unavailable; the current request can still use value. */
    }
  }

  function clearSid() {
    try {
      localStorage.removeItem(SID_KEY);
    } catch (e) {
      /* noop */
    }
  }

  async function discoverSid(productUrl) {
    if (!productUrl) return null;

    try {
      var response = await fetch(productUrl, { cache: 'no-store' });
      if (!response.ok) return null;

      var html = await response.text();
      var match = html.match(/<product-recommendations\b[^>]*\bdata-section-id=["']([^"']+)["']/i);
      var sid = match && match[1] ? match[1].trim() : '';
      if (!sid) return null;

      setSid(sid);
      return sid;
    } catch (e) {
      return null;
    }
  }

  /* 🔴 按 (section, 产品, intent) 缓存整段 HTML。
     以前每次 cart:update 都重新下载一遍这个 section 的渲染结果 ——
     实测 limit=10 时返回 420,397 字符、约 900ms，改个数量就再来一次，纯浪费。
     🔴 limit 写 10：接口上限就是 10，实测传 20 也只回 10。 */
  var htmlCache = {};

  /* 🔴 推荐内容只由两件事决定：**推荐源产品**（车里第一件）和**车里已有哪些产品**
     （已在车里的要滤掉）。改数量两者都不变，可是以前每次 cart:update 都会把
     所有卡片重新注入一遍 —— 实测点一下加号就重建 48 张色卡图 + 24 张主图，
     1.5 秒后 complementary 那批再来一次。图都命中缓存，但 DOM 重建和图片解码跑不掉，
     这就是「加减产品也会卡顿、缩略图有变动效果」的主要来源。
     指纹没变就一个节点都不动。 */
  var lastCartSig = '';
  /* 现在推荐位上那批卡是按哪份清单排除的（handle → true）。清单只少不多（删了商品）时推荐不用换：
     被删的商品本来就被排除了，不会出现在推荐里；重拉只会让推荐区整块换一遍、卡顿一下（2026-09-28 用户要求去掉）。 */
  var shownExcluded = null;
  var onlyRemoved = function (excluded) {
    if (!shownExcluded) return false;
    return Object.keys(excluded).every(function (handle) { return shownExcluded[handle]; });
  };

  /* 🔴 但「不动」做不到：morph 会把注进去的卡片整片删掉（服务端 HTML 里这个格子是空的），
     所以每次购物车变动都得重新放回去 —— 以前是**重新克隆一整批卡片**。
     改成把整块 wrap 节点存下来，内容没变时把**同一批节点**原样挂回去：
     不重新克隆、图片不重新解码，加减数量时推荐位就彻底不动了。 */
  var wrapCache = [];

  async function fetchCards(sid, productId, intent) {
    if (!sid || !productId) return null;

    var key = sid + '|' + productId + '|' + (intent || 'related');
    if (typeof htmlCache[key] === 'string') return htmlCache[key];

    try {
      var response = await fetch(
        '/recommendations/products?section_id=' +
          encodeURIComponent(sid) +
          '&product_id=' +
          encodeURIComponent(productId) +
          '&limit=10&intent=' +
          encodeURIComponent(intent || 'related')
      );
      if (!response.ok) return null;

      var text = await response.text();
      htmlCache[key] = text;
      return text;
    } catch (e) {
      return null;
    }
  }

  function parseCards(html) {
    if (!html) return null;

    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var list = doc.querySelector('.resource-list');
      if (!list) return null;

      var seen = {};
      var items = [];
      Array.from(list.querySelectorAll('product-card[data-product-id]')).forEach(function (card) {
        var productId = card.getAttribute('data-product-id');
        if (!productId || seen[productId]) return;

        var item = card.closest('.resource-list__item') || card;
        seen[productId] = true;
        items.push({ element: item, productId: String(productId) });
      });

      /* A 200 response without native cards is treated as a stale/invalid id. */
      return items.length ? { list: list, items: items } : null;
    } catch (e) {
      return null;
    }
  }

  async function loadCards(productId, productUrl, intent) {
    var sid = getSid();
    var parsed = null;

    /* Use the localStorage id first. Any HTTP or card-parse failure invalidates it. */
    if (sid) {
      parsed = parseCards(await fetchCards(sid, productId, intent));
      if (parsed) return parsed;
      clearSid();
    }

    /* No cached id, or the cached id failed: discover once from the PDP. */
    sid = await discoverSid(productUrl);
    if (!sid) return null;

    parsed = parseCards(await fetchCards(sid, productId, intent));
    if (!parsed) {
      clearSid();
      return null;
    }
    return parsed;
  }

  /*
   * Native quick-add is already rendered inside these cards: _product-card-gallery.liquid
   * renders 'quick-add' when settings.quick_add or settings.mobile_quick_add is enabled,
   * and quick-add.liquid provides the <quick-add-component> and product form. The
   * injected custom elements are upgraded by the theme's quick-add.js, so no duplicate
   * cart button is added here.
   */

  /* Keep card ids and their references unique after several native cards share block ids. */
  function namespaceIds(root, index) {
    var prefix = 'insrec-' + index + '-';
    var map = {};

    /* 🔴 必须走 getAttribute/setAttribute，不能用 element.id。
       HTMLFormElement 有 [LegacyOverrideBuiltIns] 命名获取器：quick-add 的加购表单里
       有一个 <input name="id">（Shopify 加购必需的变体字段），于是 form.id 拿到的是
       **那个 input 元素**而不是字符串 —— oldId.indexOf 抛 TypeError，
       被 fill() 的 catch 吞掉，推荐位永远空着。
       实测 2026-09-20：抽屉推荐位和购物车页推荐位都因此一直是空的。 */
    var readId = function (element) {
      var value = element.getAttribute('id');
      return typeof value === 'string' ? value : '';
    };

    var rootId = readId(root);
    if (rootId) {
      map[rootId] = prefix + rootId;
      root.setAttribute('id', map[rootId]);
    }

    root.querySelectorAll('[id]').forEach(function (element) {
      var oldId = readId(element);
      if (!oldId || oldId.indexOf(prefix) === 0) return;
      map[oldId] = prefix + oldId;
      element.setAttribute('id', map[oldId]);
    });

    if (!Object.keys(map).length) return;

    var references = ['for', 'form', 'list', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'aria-owns'];
    root.querySelectorAll('*').forEach(function (element) {
      references.forEach(function (attribute) {
        var value = element.getAttribute && element.getAttribute(attribute);
        if (!value) return;
        var next = value
          .split(/\s+/)
          .map(function (token) { return map[token] || token; })
          .join(' ');
        if (next !== value) element.setAttribute(attribute, next);
      });

      var href = element.getAttribute && element.getAttribute('href');
      if (href && href.charAt(0) === '#' && map[href.slice(1)]) {
        element.setAttribute('href', '#' + map[href.slice(1)]);
      }
    });
  }

  /* 用户 2026-09-20：推荐位要 **6 行**。列数是 CSS 决定的（抽屉 2 列、购物车页 3 列、
     手机 2 列），所以先把空的 wrap 放进 DOM 量一次列数，再决定放几张卡。 */
  var TARGET_ROWS = 6;

  function columnsOf(element) {
    var value = getComputedStyle(element).gridTemplateColumns || '';
    var count = value.split(' ').filter(function (part) {
      return part && part !== 'none';
    }).length;
    return count > 0 ? count : 2;
  }

  /* ══════ 色卡提前收好 ══════
     🔴 色卡「先全部摆出来、再闪一下收成 +5」（用户 2026-09-27 反馈；逐帧实测 9 色卡片第一次可见时
     一个都没收，120~180ms 后才收起）。主题 <overflow-list> 要等 IntersectionObserver 报
     「进入视口附近」才收多余色卡、还再推迟一个任务；它那 640px 提前量**只对整页滚动有效** ——
     抽屉里的推荐位被抽屉自己的滚动容器裁切，要真滚进可视区才触发。
     做法：
       · 此刻看得见的卡片：注入后（绘制之前）当场收；
       · 其余：浏览器空闲时分批收（requestIdleCallback，每批几张），赶在用户滚到之前收完；
       · 兜底：以抽屉滚动容器为根的 IntersectionObserver —— 空闲批次还没轮到就被滚到附近的，当场收。
       🔴 别等「快滚到了」才收（试过）：主题自己在卡片滚进视野时还会再收一遍，两次叠在滚动过程中，
         长动画帧实测比原版还多。也别在注入那一帧一口气全收：会把本来就重的注入帧再拉长 50ms。
         （单次收纳在布局干净时只要 4~5ms；LoAF 里看到的 50~110ms 多是被它提前结算的积压布局。）
     🔴🔴 触发收纳前必须先把列表高度定成「第一个色块的高度」—— 主题自己在 IO 回调里就是这么做的
     （#reflowItems(entry.boundingClientRect.height)），而组件的 reflow 事件那条路传的是 0。
     不定高度时组件按最窄排版、每个色块各占一行，结果只剩 1 个色块 +N，连放得下的 3、4 色也被收
     （实测 8 色卡：不定高 → 1+7、宽 63px；定高 26px → 4+4、宽 163px，和主题自然收起的一致）。
     组件要等自己的样式表加载完才初始化，那之前事件没人接 —— 没收上的下一帧再试。 */
  var swatchObservers = [];
  var swatchRetry = [];
  var swatchRetryFrames = 0;

  function firstSwatch(ol) {
    for (var i = 0; i < ol.children.length; i += 1) {
      if (ol.children[i].tagName !== 'TEMPLATE') return ol.children[i];
    }
    return null;
  }

  /** 按主题的算法当场收好一个色卡列表。true = 收好了（或不用再管）；false = 组件还没初始化，稍后再试 */
  function settleSwatch(ol) {
    if (!ol.isConnected || ol.__insmiaSettled) return true;
    var list = ol.shadowRoot && ol.shadowRoot.querySelector('[part="list"]');
    var first = firstSwatch(ol);
    var itemHeight = first ? first.getBoundingClientRect().height : 0;
    if (!list || !itemHeight) return false;
    list.style.setProperty('height', itemHeight + 'px');
    try {
      ol.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
    } catch (e) { /* 组件异常不影响推荐位本身 */ }
    if (ol.style.getPropertyValue('--overflow-count') === '') {
      list.style.removeProperty('height'); // 组件还没接住事件：高度别留着
      return false;
    }
    ol.__insmiaSettled = true;
    return true;
  }

  /** 推荐盒所在的滚动容器（抽屉里是抽屉的列表区；购物车页是整页 = null）。每个盒子只算一次 */
  function scrollRootOf(box) {
    if (box.__insmiaScrollRoot !== undefined) return box.__insmiaScrollRoot;
    var root = null;
    for (var node = box.parentElement; node && node !== document.body; node = node.parentElement) {
      if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) { root = node; break; }
    }
    box.__insmiaScrollRoot = root;
    return root;
  }

  function swatchObserverFor(root) {
    for (var i = 0; i < swatchObservers.length; i += 1) {
      if (swatchObservers[i].root === root) return swatchObservers[i].io;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.target.__insmiaSettled) { io.unobserve(entry.target); return; }
        if (!entry.isIntersecting) return;
        if (settleSwatch(entry.target)) io.unobserve(entry.target);
        else retrySwatch(entry.target);
      });
    }, { root: root, rootMargin: '600px 0px 600px 0px' });
    swatchObservers.push({ root: root, io: io });
    return io;
  }

  /* 空闲时分批收（Safari 没有 requestIdleCallback：退回每次 2 张的 setTimeout） */
  var idleSwatches = [];
  var idleScheduled = false;
  function scheduleIdleSwatches() {
    if (idleScheduled || !idleSwatches.length) return;
    idleScheduled = true;
    var run = function (deadline) {
      idleScheduled = false;
      var budget = 2;
      while (idleSwatches.length && (deadline ? deadline.timeRemaining() > 6 : budget-- > 0)) {
        var ol = idleSwatches.shift();
        if (!ol.isConnected || ol.__insmiaSettled) continue;
        if (!ol.getBoundingClientRect().width) continue; // 抽屉关了：留给 IO / 抽屉打开时再收
        if (!settleSwatch(ol)) retrySwatch(ol);
      }
      scheduleIdleSwatches();
    };
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 500 });
    else window.setTimeout(function () { run(null); }, 16);
  }

  function retrySwatch(ol) {
    if (swatchRetry.indexOf(ol) === -1) swatchRetry.push(ol);
    if (swatchRetry.length > 1) return; // 已经排上下一帧了
    requestAnimationFrame(function () {
      var lists = swatchRetry;
      swatchRetry = [];
      if ((swatchRetryFrames += 1) > 90) { swatchRetryFrames = 0; return; } // 兜底：最多试 ~1.5 秒
      placeSwatches(lists);
    });
  }

  /** 看得见（或 100px 内就要看见）的当场收；其余排进空闲批次，IntersectionObserver 兜底 */
  function placeSwatches(lists) {
    lists = lists.filter(function (ol) { return ol.isConnected && !ol.__insmiaSettled; });
    if (!lists.length) { swatchRetryFrames = 0; return; }
    // 先把位置统一读完再动手，读写不交错
    var plans = lists.map(function (ol) {
      var box = ol.closest('[data-insmia-recs]');
      var root = box ? scrollRootOf(box) : null;
      var view = root ? root.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
      return { ol: ol, root: root, rect: ol.getBoundingClientRect(), view: view };
    });
    plans.forEach(function (plan) {
      var r = plan.rect;
      var near = r.width > 0 && r.bottom > plan.view.top - 100 && r.top < plan.view.bottom + 100;
      if (near) {
        if (!settleSwatch(plan.ol)) retrySwatch(plan.ol);
        return;
      }
      // 看不见的：空闲时收；万一空闲批次还没轮到就被滚到附近，由 IO 当场收
      // （抽屉关着、宽度为 0 的，空闲批次会跳过，等 IO 或抽屉打开时的 watchSwatches）
      if (r.width > 0 && idleSwatches.indexOf(plan.ol) === -1) idleSwatches.push(plan.ol);
      swatchObserverFor(plan.root).observe(plan.ol);
    });
    scheduleIdleSwatches();
  }

  function watchSwatches(root) {
    if (!root || !('IntersectionObserver' in window)) return;
    placeSwatches([].slice.call(root.querySelectorAll('overflow-list')));
  }

  function injectInto(box, boxIndex, list, items) {
    var grid = box.querySelector('[data-insmia-recs-grid]');
    if (!grid) return 0;

    var wrap = document.importNode(list, false);
    wrap.classList.remove('resource-list__carousel', 'force-full-width');
    wrap.classList.add('resource-list--grid');

    grid.innerHTML = '';
    grid.appendChild(wrap);

    var capacity = columnsOf(wrap) * TARGET_ROWS;

    items.slice(0, capacity).forEach(function (item, index) {
      wrap.appendChild(prepareCard(item, boxIndex, index));
    });
    // 组件初始化是微任务，排在它后面再收；没接上的由 rAF 在下一次绘制前补
    Promise.resolve().then(function () { watchSwatches(wrap); });

    // 存下这一整块，下次购物车没变时直接挂回去（见 fill 里的指纹分支）
    wrapCache[boxIndex] = wrap;

    box.hidden = items.length === 0;
    box.__insmiaState = items.length ? 'shown' : 'hidden';
    guardBox(box, boxIndex);
    return capacity;
  }

  /** 克隆一张推荐卡（挂载点序号 + 位置决定 id 前缀，见 namespaceIds 的说明） */
  function prepareCard(item, boxIndex, index) {
    var clone = document.importNode(item.element, true);
    if (!clone.matches('.resource-list__item')) {
      var itemWrapper = document.createElement('div');
      itemWrapper.className = 'resource-list__item';
      itemWrapper.appendChild(clone);
      clone = itemWrapper;
    }
    /* 🔴 前缀必须掺进挂载点序号。同一批卡片注进抽屉和页面两处，
       只用 index 的话两边 id 一模一样 —— morph 的 getNodeKey 回落到 node.id，
       重号会让它配错节点、把真实商品行删掉（"加购的产品消失"就是这么来的）。 */
    namespaceIds(clone, boxIndex * 100 + index);
    /* 🔴🔴 性能：每张原生 PLP 卡自带一整个媒体轮播。实测单卡 7 个 slide / 7 张图，
       12~18 张卡就是上百个 slide、上百张图，滚到推荐位必卡。
       推荐卡只需要「首图 + hover 第二张」，第 3 张往后全删。
       卡上的轮播控件是左右箭头、没有「1/7」这种计数，删了不会留下错数字。 */
    var slides = clone.querySelectorAll('slideshow-slide');
    for (var slideIndex = 2; slideIndex < slides.length; slideIndex += 1) {
      slides[slideIndex].remove();
    }

    /* 推荐位永远在商品列表下面，图片一律懒加载 + 低优先级，别和首屏抢带宽 */
    clone.querySelectorAll('img').forEach(function (img) {
      img.setAttribute('loading', 'lazy');
      img.setAttribute('fetchpriority', 'low');
    });
    return clone;
  }

  /**
   * 🔴 同帧兜底：任何一次购物车变动，morph 都会把推荐格子清空、给盒子加回 hidden
   * （服务端 HTML 里格子是空的、盒子默认 hidden）。以前要等 schedule() 的 700ms 后 fill()
   * 才挂回来 —— 这 0.7 秒里推荐区（抽屉里 1600 多像素高）整块塌掉再弹回，
   * 勾选、全选、改数量、换尺码每一下都「卡」一下（用户 2026-09-26 反馈）。
   * MutationObserver 回调是微任务、在浏览器绘制之前执行：在这里把上一批节点挂回去，
   * 塌陷一帧都不会露出来。内容要不要换，交给随后的 fill() 按指纹决定。
   * 状态记在 JS 属性上而不是 data-*：data-* 会被同一次 morph 按服务端 HTML 抹掉。
   */
  function guardBox(box, index) {
    if (box.__insmiaGuard) return;
    var grid = box.querySelector('[data-insmia-recs-grid]');
    if (!grid) return;
    var restore = function () {
      /* 🔴🔴 盒子已经不在页面上（被 morph 摘掉了）就撤掉守卫、什么都不做。
         「全不选」时抽屉换成空车模板，旧盒子被摘掉但守卫还挂着；新盒子把缓存挂过去后，
         旧守卫会把它抢回旧格子、新守卫再抢回来，两边在微任务里无限往复 ⇒ 页面卡死
         （2026-09-27 实测：点全不选后主线程死循环，调用栈 restore → disconnectedCallback）。 */
      if (!box.isConnected) {
        box.__insmiaGuard.disconnect();
        return;
      }
      var cached = wrapCache[index];
      if (!cached || box.__insmiaState !== 'shown') return;
      if (!grid.contains(cached) && !grid.querySelector('.resource-list__item')) grid.appendChild(cached);
      if (box.hidden) box.hidden = false;
    };
    box.__insmiaGuard = new MutationObserver(restore);
    box.__insmiaGuard.observe(box, { attributes: true, attributeFilter: ['hidden'] });
    box.__insmiaGuard.observe(grid, { childList: true });
  }

  /** 立刻（同步）把缓存挂回每个空着的盒子 —— 新换上来的盒子（比如全不选时补的挂载点）也不空着等 700ms */
  function restoreNow() {
    document.querySelectorAll('[data-insmia-recs]').forEach(function (box, index) {
      var grid = box.querySelector('[data-insmia-recs-grid]');
      var cached = wrapCache[index];
      if (!grid || !cached) return;
      if (!box.__insmiaState) box.__insmiaState = 'shown';
      if (box.__insmiaState !== 'shown') return;
      if (!grid.contains(cached) && !grid.querySelector('.resource-list__item')) {
        grid.innerHTML = '';
        grid.appendChild(cached);
      }
      box.hidden = false;
      guardBox(box, index);
    });
  }

  var handleOf = function (url) {
    var tail = String(url || '').split('/products/')[1] || '';
    return tail.split(/[?#]/)[0];
  };

  /**
   * 页面上显示的整张清单：勾选的 + 取消勾选的幽灵行，按显示顺序、按商品去重。
   * 同一个页面只会有一个购物车表（抽屉或购物车页），全不选时就是幽灵表。
   */
  /* 购物车的记录顺序：和 insmia-cart-select.js 摆行用的是同一份（它没加载完时直接读存储） */
  var ORDER_KEY = 'insmia:cart:order:v1';
  function storedOrder() {
    var api = window.insmiaCartOrder;
    try {
      var list = api && typeof api.read === 'function' ? api.read() : JSON.parse(localStorage.getItem(ORDER_KEY) || '[]');
      return Array.isArray(list) ? list.map(String) : [];
    } catch (e) {
      return [];
    }
  }

  function listedProducts() {
    var table = document.querySelector('.cart-items__table');
    if (!table) return [];
    var seen = {};
    var out = [];
    /* 🔴 按购物车的**记录顺序**取，不按此刻 DOM 里的先后：页面刚加载时（insmia-cart-select.js
       还没把行按记录顺序摆好之前）DOM 是 Shopify 的顺序（最后加购的在最前）。推荐位要是在这时算锚点，
       等行摆好后，下一次算出来的锚点就不同 ⇒ 勾一下 / 取消一下，推荐区被整批重拉，
       先少一行（高度缩 277px）、1.5 秒后再补回来（2026-09-27 实测：加载时 [Adria, Spoleto, Adriana]，
       0.5 秒后才摆成 [Spoleto, Adria, Adriana]；勾选脚本加载慢的时候推荐位先算了）。
       记录里没有的（刚加购的新品）排最前，和摆行的规则一致。 */
    var known = storedOrder();
    var resolve = window.insmiaCartOrder && typeof window.insmiaCartOrder.resolve === 'function'
      ? function (id) { return String(window.insmiaCartOrder.resolve(id)); }
      : function (id) { return String(id); };
    var rows = [].slice.call(table.querySelectorAll('.cart-items__table-row[data-variant-id]'))
      .map(function (row, index) { return { row: row, index: index, slot: known.indexOf(resolve(row.dataset.variantId)) }; })
      .sort(function (a, b) { return (a.slot - b.slot) || (a.index - b.index); })
      .map(function (entry) { return entry.row; });
    rows.forEach(function (row) {
      var link = row.querySelector('a[href*="/products/"]');
      var href = link ? link.getAttribute('href') : '';
      var handle = handleOf(href);
      if (!handle || seen[handle]) return;
      seen[handle] = true;
      out.push({ handle: handle, url: href });
    });
    return out;
  }

  var productIdCache = {};
  async function productIdOf(entry, cart) {
    if (productIdCache[entry.handle]) return productIdCache[entry.handle];
    var hit = (cart && cart.items || []).find(function (item) { return item.handle === entry.handle; });
    if (hit && hit.product_id) return (productIdCache[entry.handle] = hit.product_id);
    var product = await (await fetch('/products/' + encodeURIComponent(entry.handle) + '.js')).json();
    return (productIdCache[entry.handle] = product.id);
  }

  async function fill() {
    /* 🔴 querySelectorAll 不是 querySelector：购物车**页**上抽屉和页面各有一个挂载点，
       只填第一个的话（抽屉在 header 里、DOM 顺序更靠前）页面那个永远填不上。 */
    var boxes = [].slice.call(document.querySelectorAll('[data-insmia-recs]'));
    if (!boxes.length) return;

    /* 还没打开抽屉、也没滚到推荐位，就不该为它下载几百 KB */
    if (!wanted) return;

    var hideAll = function () {
      boxes.forEach(function (b) { b.hidden = true; b.__insmiaState = 'hidden'; });
    };

    try {
      /* 🔴 推荐依据 = 页面上显示的整张清单（含取消勾选的幽灵行），不是 Shopify 的 cart.items：
         cart.items 的顺序每次加回商品都会变（/cart/add.js 往前插），而且取消勾选的商品不在里面 ——
         按它算的话，勾选一变锚点就变、整批推荐换掉，刚取消勾选的商品还会被推荐出来。 */
      var listed = listedProducts();
      if (!listed.length) { hideAll(); return; }

      var anchor = listed[0];
      var excluded = {};
      listed.forEach(function (entry) { excluded[entry.handle] = true; });

      /* 指纹一致 = 推荐结果必然一致，把上一批节点原样挂回去就行，不用重建。 */
      var cartSig = anchor.handle + '|' + Object.keys(excluded).sort().join(',');
      /* 指纹一致，或者只是删了商品（清单是上次那份的子集）：把上一批节点原样挂回去，不重拉。
         🔴 新加了清单里没有的商品才重拉 —— 它可能正好在推荐位里，购物车里的东西不能再出现在「You may also like」。 */
      if (cartSig === lastCartSig || onlyRemoved(excluded)) {
        var restoredAll = true;
        boxes.forEach(function (box, index) {
          if (box.querySelector('.resource-list__item')) { box.hidden = false; box.__insmiaState = 'shown'; guardBox(box, index); return; }
          var grid = box.querySelector('[data-insmia-recs-grid]');
          var cached = wrapCache[index];
          if (!grid || !cached) { restoredAll = false; return; }
          grid.innerHTML = '';
          grid.appendChild(cached);
          box.hidden = false;
          box.__insmiaState = 'shown';
          guardBox(box, index);
        });
        // 没缓存（首次，或挂载点变了）就老老实实重建一次
        if (restoredAll) return;
      }

      var cart = null;
      try { cart = await (await fetch('/cart.js')).json(); } catch (e) { cart = null; }
      var anchorId = await productIdOf(anchor, cart);

      var data = await loadCards(anchorId, anchor.url, 'related');
      if (!data) { hideAll(); return; }

      var seen = {};
      var keep = function (item) {
        var link = item.element.querySelector('a[href*="/products/"]');
        var handle = link ? handleOf(link.getAttribute('href')) : '';
        if ((handle && excluded[handle]) || seen[item.productId]) return false;
        seen[item.productId] = true;
        return true;
      };
      var items = data.items.filter(keep);
      if (!items.length) { hideAll(); return; }

      var capacity = 0;
      boxes.forEach(function (box, index) {
        capacity = Math.max(capacity, injectInto(box, index, data.list, items));
      });
      // 真的注入成功了才记指纹
      lastCartSig = cartSig;
      shownExcluded = excluded;

      /* 接口一次最多 10 条，铺不满 6 行就再补一批 complementary。
         🔴 这一批**延后**拉：首屏先有东西，别为了第 6 行让用户等第二个 400KB。 */
      if (items.length >= capacity) return;

      window.setTimeout(async function () {
        if (lastCartSig !== cartSig) return; // 这 1.5 秒里清单变了，这批已经作废
        var extra = await loadCards(anchorId, anchor.url, 'complementary');
        if (!extra) return;

        var fresh = [];
        extra.items.forEach(function (item) {
          if (!keep(item)) return;
          items.push(item);
          fresh.push(item);
        });
        if (!fresh.length) return;

        /* 🔴 只把新卡片**追加**到已有那批后面，不要整批重建：重建 = 前 10 张节点全换、
           色卡全部重新收一遍，正看着推荐位的人会看到整块闪一下。挂载点换过了（缓存不在格子里）才重建。 */
        boxes.forEach(function (box, index) {
          var wrap = wrapCache[index];
          var grid = box.querySelector('[data-insmia-recs-grid]');
          if (!wrap || !grid || !grid.contains(wrap)) {
            injectInto(box, index, data.list, items);
            return;
          }
          var room = columnsOf(wrap) * TARGET_ROWS - wrap.children.length;
          fresh.slice(0, Math.max(0, room)).forEach(function (item, offset) {
            wrap.appendChild(prepareCard(item, index, wrap.children.length + offset));
          });
          Promise.resolve().then(function () { watchSwatches(wrap); });
        });
      }, 1500);
    } catch (e) {
      hideAll();
    }
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fill, 700);
  }

  function trigger() {
    wanted = true;
    schedule();
  }

  function watchDrawer() {
    document.querySelectorAll('cart-drawer-component dialog').forEach(function (dialog) {
      if (dialog.dataset.insRecsWatched === '1') return;
      dialog.dataset.insRecsWatched = '1';
      new MutationObserver(function () {
        if (!dialog.hasAttribute('open')) return;
        // 抽屉关着时注入的卡片量不到宽度、色卡没收；打开的这一刻（绘制之前）把看得见的补收
        watchSwatches(dialog);
        trigger();
      }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
    });
  }

  /* 🔴🔴 性能：以前是 DOMContentLoaded + 700ms **无条件**去拉推荐，
     而购物车抽屉在**每个页面**都渲染 —— 于是全站每次页面加载都白下载一次
     推荐区块的 section HTML（实测 limit=10 返回 420,397 字符、约 900ms），
     哪怕用户根本没点开过购物车。
     现在改成「要看见了才拉」：
       · 抽屉：等 <dialog> 真的 open（加购自动弹抽屉也算）
       · 购物车页：推荐位在页面流里，用 IntersectionObserver 滚到附近才拉
     抽屉那个盒子在关闭的 dialog 里（display:none）永远不会 intersect，
     正好各管各的，不会重复触发。 */
  var viewObserver = null;

  /* 🔴 不能直接观察 [data-insmia-recs] 本身：它默认带 hidden（display:none），
     而 display:none 的元素**永远不会 intersect** —— 实测购物车页滚到底也不加载。
     改成在盒子前面插一个 1px 的哨兵，哨兵在正常文档流里，位置恰好就是推荐位所在处。
     抽屉里的哨兵在关闭的 <dialog> 内，同样 display:none、同样不会触发，
     那一路由 watchDrawer 的 open 观察器负责，两边各管各的。 */
  function observeSentinels() {
    if (wanted || !('IntersectionObserver' in window)) return;

    if (!viewObserver) {
      viewObserver = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              viewObserver.disconnect();
              trigger();
              return;
            }
          }
        },
        { rootMargin: '800px' }
      );
    }

    document.querySelectorAll('[data-insmia-recs]').forEach(function (box) {
      if (box.dataset.insRecsSentinel === '1') return;
      box.dataset.insRecsSentinel = '1';

      var sentinel = document.createElement('span');
      sentinel.className = 'insmia-recs-sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      box.parentNode.insertBefore(sentinel, box);
      viewObserver.observe(sentinel);

      /* 🔴 兜底：推荐位如果在正常文档流里（= 购物车页，哨兵的 offsetParent 不为空），
         2.5 秒后就直接加载，不把「能不能看到」全押在 IntersectionObserver 上。
         IO 在页面不可见时根本不回调（实测 visibilityState=hidden 时完全不触发），
         真出点岔子就变成推荐位永远空着。
         抽屉里的哨兵在关闭的 <dialog> 内，display:none ⇒ offsetParent 为 null，
         不会命中这条兜底，仍然是「打开抽屉才拉」。 */
      if (sentinel.offsetParent !== null) {
        window.setTimeout(function () {
          if (!wanted) trigger();
        }, 2500);
      }
    });
  }

  function arm() {
    watchDrawer();
    observeSentinels();
    if (armed) return;
    armed = true;

    if (!('IntersectionObserver' in window)) trigger();
  }

  // 全不选时 insmia-cart-select.js 给空车模板补了推荐位挂载点，让这里去填
  document.addEventListener('insmia:recs-refill', function () {
    watchDrawer();
    restoreNow(); // 全不选时补上来的新挂载点：先把上一批挂上，别空着等 700ms
    if (wanted) schedule();
  });

  document.addEventListener('cart:update', function () {
    restoreNow(); // 有的更新流程是先 morph 后发事件：格子已经被清空了，同步挂回
    watchDrawer();
    // section 重渲染会把哨兵冲掉，没触发过就重新插一个
    observeSentinels();
    if (wanted) schedule();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arm);
  } else {
    arm();
  }
})();
